import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  GET_BUNDLE_GROUPS,
  GET_PRODUCT_VARIANTS_FOR_WIZARD,
  GET_VARIANT_OPTIONS_FOR_WIZARD
} from '../../graphql/product-queries'
import { Product, ProductVariant } from '../../types/product'
import { CartItem, WizardState } from '../../types/cart'
import ChooseVariantStep from './ChooseVariantStep'
import CustomizeStep from './CustomizeStep'
import { optionItemSoldOut, withoutSoldOutOptions } from '../../lib/availability'
import { useSoldOutProducts } from '../../hooks/useProductAvailability'
import { useDropSoldOutOptions } from '../../hooks/useDropSoldOutOptions'

interface BundleWizardProps {
  product: Product // bundle product
  isOpen: boolean
  onClose: () => void
  onAddToCart: (wizardState: WizardState) => void
  editItem?: CartItem
  editChildren?: CartItem[]
}

const BundleWizard: React.FC<BundleWizardProps> = ({ product, isOpen, onClose, onAddToCart, editItem, editChildren = [] }) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [selectedMainProduct, setSelectedMainProduct] = useState<{ id: number; name: string } | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number[]>>({})
  const [selectedMealParts, setSelectedMealParts] = useState<Record<number, number>>({})

  // Fetch bundle groups for this bundle product
  const { data: bundleGroupsResp, loading: bundleGroupsLoading } = useQuery(
    GET_BUNDLE_GROUPS,
    {
      variables: { bundleProductId: Number(product.id) },
      skip: !isOpen
    }
  )

  const allGroups = bundleGroupsResp?.bundleGroups || []
  const mainGroup = useMemo(() => {
    return (allGroups || []).find((g: any) => typeof g.name === 'string' && g.name.toLowerCase().includes('main'))
  }, [allGroups])
  const otherGroups = useMemo(() => {
    return (allGroups || []).filter((g: any) => !String(g.name || '').toLowerCase().includes('main'))
  }, [allGroups])

  // Build unique main products list from main group options
  const mainProducts = useMemo(() => {
    if (!mainGroup) return [] as { id: number; name: string; soldOut: boolean }[]
    const map = new Map<number, { name: string; soldOut: boolean }>()
    ;(mainGroup.options || []).forEach((opt: any) => {
      const prod = opt?.eligible_variant?.product
      if (prod?.id && prod?.name) {
        map.set(Number(prod.id), { name: prod.name, soldOut: !!prod.sold_out })
      }
    })
    return Array.from(map.entries()).map(([id, p]) => ({ id, name: p.name, soldOut: p.soldOut }))
  }, [mainGroup])

  // Fetch variants for selected main product
  const { data: variantsData, loading: variantsLoading } = useQuery(
    GET_PRODUCT_VARIANTS_FOR_WIZARD,
    {
      variables: { productId: Number(selectedMainProduct?.id) },
      skip: !isOpen || !selectedMainProduct
    }
  )
  const variants: ProductVariant[] = (variantsData?.productVariants || []).map((v: any) => ({
    ...v,
    id: Number(v.id)
  }))

  // Options for selected variant
  const { data: optionsData, loading: optionsLoading } = useQuery(
    GET_VARIANT_OPTIONS_FOR_WIZARD,
    {
      variables: { variantId: Number(selectedVariantId!) },
      skip: !isOpen || !selectedVariantId
    }
  )
  const productSoldOut = useSoldOutProducts()
  const optionGroupsWithItems = useMemo(() => (
    optionsData?.variantOptionGroupsByVariant?.map((group: any) => ({
      ...group,
      option_group: {
        ...group.option_group,
        id: Number(group.option_group.id),
        option_items: (group.option_group.option_items || []).map((item: any) => ({
          ...item,
          id: Number(item.id),
          // The linked product's live mark from the register's availability poll; the API's own
          // field is the fallback for a product the poll does not carry.
          sold_out: optionItemSoldOut(item, productSoldOut)
        }))
      }
    })) || []
  ), [optionsData, productSoldOut])

  // Pre-populate state from cart item being edited (runs once bundle groups have loaded)
  useEffect(() => {
    if (!editItem || !isOpen || !bundleGroupsResp) return
    // Find the main product by matching eligible_variant.id to editItem's variant
    const editVariantId = editItem.product_variant_id
    let foundProduct: { id: number; name: string } | null = null
    for (const grp of allGroups) {
      if (!String(grp.name || '').toLowerCase().includes('main')) continue
      for (const opt of grp.options || []) {
        if (Number(opt.eligible_variant?.id) === Number(editVariantId)) {
          const prod = opt.eligible_variant?.product
          if (prod?.id) foundProduct = { id: Number(prod.id), name: String(prod.name || '') }
          break
        }
      }
      if (foundProduct) break
    }

    const optionsRecord: Record<number, number[]> = {}
    for (const o of editItem.selected_options || []) {
      if (!optionsRecord[o.option_group_id]) optionsRecord[o.option_group_id] = []
      optionsRecord[o.option_group_id].push(o.option_item_id)
    }

    const mealParts: Record<number, number> = {}
    for (const child of editChildren) {
      if (child.type === 'meal_part' && child.bundle_group_id !== undefined) {
        mealParts[child.bundle_group_id] = child.product_variant_id
      }
    }

    if (foundProduct) setSelectedMainProduct(foundProduct)
    setSelectedVariantId(editVariantId)
    setSelectedOptions(optionsRecord)
    setSelectedMealParts(mealParts)
    setCurrentStep(steps.length || 3) // jump to last step (customize or otherParts)
  }, [editItem, isOpen, bundleGroupsResp])

  // Sold-out choices cannot stay chosen: their buttons are disabled, so a main or part restored
  // by the edit pre-fill above, or one that sold out mid-wizard (the register's 30 s poll updates
  // the same Product entries), could not be changed and would only fail at checkout. Declared
  // after the pre-fill so its updater runs on the pre-filled state.
  useEffect(() => {
    if (selectedMainProduct && mainProducts.find(mp => mp.id === selectedMainProduct.id)?.soldOut) {
      setSelectedMainProduct(null)
      setSelectedVariantId(null)
      setCurrentStep(1)
    }
  }, [mainProducts, selectedMainProduct])

  useEffect(() => {
    setSelectedMealParts(prev => {
      const kept = Object.entries(prev).filter(([gid, vid]) => {
        const group = (otherGroups || []).find((g: any) => Number(g.id) === Number(gid))
        const option = (group?.options || []).find((o: any) => Number(o.eligible_variant?.id) === Number(vid))
        return !option?.eligible_variant?.product?.sold_out
      })
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept)
    })
  }, [otherGroups, selectedMealParts])

  // Step validation and navigation
  const steps: Array<'chooseMain' | 'chooseVariant' | 'customize' | 'otherParts'> = useMemo(() => {
    const s: Array<'chooseMain' | 'chooseVariant' | 'customize' | 'otherParts'> = []
    s.push('chooseMain')
    s.push('chooseVariant')
    s.push('customize')
    if ((otherGroups || []).length > 0) s.push('otherParts')
    return s
  }, [otherGroups])
  const currentStepId = steps[currentStep - 1]

  // The same for a customization option of the main (see the hook): `OptionItem.sold_out` follows
  // its linked product's mark, derived from the register's poll. Drop it and go back to customize.
  useDropSoldOutOptions(optionGroupsWithItems, selectedOptions, soldOut => {
    setSelectedOptions(prev => withoutSoldOutOptions(prev, soldOut))
    setCurrentStep(steps.indexOf('customize') + 1)
  })

  // Every option group at or above its min_select. Checked on the customize step AND before the
  // final Add: a sold-out drop can empty a required group while the cashier is on a later step,
  // and the API does not validate min_select at order time.
  const optionsSatisfied = optionGroupsWithItems.every((group: any) => {
    const selectedItems = selectedOptions[group.option_group.id] || []
    return selectedItems.length >= group.option_group.min_select
  })

  const canProceed = () => {
    if (currentStepId === 'chooseMain') return !!selectedMainProduct
    if (currentStepId === 'chooseVariant') return selectedVariantId !== null
    if (currentStepId === 'customize') return optionsSatisfied
    if (currentStepId === 'otherParts') return true
    return false
  }

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1)
    } else {
      if (!optionsSatisfied) {
        setCurrentStep(steps.indexOf('customize') + 1)
        return
      }
      // Build selected option details for main customization
      const selectedOptionDetails = Object.entries(selectedOptions || {}).flatMap(([groupId, ids]: any) => {
        const grp = optionGroupsWithItems.find((g: any) => Number(g.option_group.id) === Number(groupId))
        const items = grp?.option_group?.option_items || []
        return (ids || []).map((id: number) => {
          const it = items.find((x: any) => Number(x.id) === Number(id))
          return it ? {
            option_group_id: Number(groupId),
            option_group_name: String(grp?.option_group?.name || ''),
            option_item_id: Number(it.id),
            option_item_label: String(it.label || ''),
            price_delta_cents: Number(it.price_delta_cents || 0)
          } : null
        }).filter(Boolean) as any[]
      })

      // Enrich with names for groups and selected meal part variants
      const bundleGroupNames: Record<number, string> = {}
      const mealVariantNames: Record<number, { productName: string; variantName: string }> = {}
      // Statiegeld of each chosen meal part — a meal-deal drink is free but its
      // can is not, and the API charges it from the variant.
      const mealVariantDeposits: Record<number, number> = {}
      // Surcharge of each chosen meal part: it sits on the group option
      // (+€0,50 for a premium drink), not on the drink's variant.
      const mealVariantPriceDeltas: Record<number, number> = {}
      try {
        (allGroups || []).forEach((grp: any) => {
          const gid = Number(grp.id)
          bundleGroupNames[gid] = String(grp.name || '')
          const selVid = selectedMealParts[gid]
          if (selVid !== undefined) {
            const opt = (grp.options || []).find((o: any) => Number(o.eligible_variant?.id) === Number(selVid))
            if (opt) {
              mealVariantNames[gid] = {
                productName: String(opt.eligible_variant?.product?.name || ''),
                variantName: String(opt.eligible_variant?.name || '')
              }
              mealVariantDeposits[gid] = Number(opt.eligible_variant?.deposit_cents || 0)
              mealVariantPriceDeltas[gid] = Number(opt.price_delta_cents || 0)
            }
          }
        })
      } catch {}

      // Add to cart using same shape as ProductWizard, with bundle main info
      const mainVariant = variants.find(v => Number(v.id) === Number(selectedVariantId))
      const wizardState: WizardState = {
        currentStep: currentStep,
        selectedProductId: Number(selectedMainProduct?.id || 0),
        selectedVariantId: selectedVariantId,
        selectedOptions: selectedOptions,
        selectedOptionDetails,
        selectedBundle: null,
        selectedMealParts: selectedMealParts,
        bundleGroupNames,
        mealVariantNames,
        mealVariantDeposits,
        mealVariantPriceDeltas,
        selectedMainProductName: selectedMainProduct?.name,
        selectedMainVariantName: mainVariant?.name,
        selectedMainVariantPriceCents: mainVariant?.price_cents,
        selectedMainVariantDepositCents: Number((mainVariant as any)?.deposit_cents || 0)
      }
      onAddToCart(wizardState)
      onClose()
    }
  }

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{product.name}</h2>
            <p className="text-sm text-gray-500">Step {currentStep} of {steps.length}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Steps */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {currentStepId === 'chooseMain' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Choose your main</h3>
              {bundleGroupsLoading && <div className="text-sm text-gray-500">Loading…</div>}
              <div className="space-y-2">
                {mainProducts.map(mp => (
                  <button
                    key={mp.id}
                    disabled={mp.soldOut}
                    onClick={() => { setSelectedMainProduct({ id: mp.id, name: mp.name }); setSelectedVariantId(null); setCurrentStep(2) }}
                    className={`w-full p-3 border rounded-lg text-left ${
                      mp.soldOut
                        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        : selectedMainProduct?.id === mp.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{mp.name}</div>
                    {mp.soldOut && <div className="text-xs font-semibold uppercase text-gray-500">Sold out</div>}
                  </button>
                ))}
                {mainProducts.length === 0 && <div className="text-sm text-gray-500">No mains available.</div>}
              </div>
            </div>
          )}

          {currentStepId === 'chooseVariant' && (
            <ChooseVariantStep
              variants={variants}
              selectedVariantId={selectedVariantId}
              onVariantSelect={(v: ProductVariant) => { setSelectedVariantId(v.id); setCurrentStep(3) }}
              loading={variantsLoading}
            />
          )}

          {currentStepId === 'customize' && (
            <CustomizeStep
              optionGroups={optionGroupsWithItems}
              selectedOptions={selectedOptions}
              onOptionsChange={(groupId: number, ids: number[]) => setSelectedOptions(prev => ({ ...prev, [groupId]: ids }))}
              loading={optionsLoading}
            />
          )}

          {currentStepId === 'otherParts' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Choose the other parts</h3>
              {(otherGroups || []).map((group: any) => (
                <div key={group.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="font-medium text-gray-900 mb-2">{group.name}</div>
                  <div className="space-y-2">
                    {(group.options || []).length > 0 && (group.options || []).every((opt: any) => opt.eligible_variant?.product?.sold_out) && (
                      <div className="text-sm text-gray-500">Everything in this part is sold out.</div>
                    )}
                    {(group.options || []).map((opt: any) => {
                      const soldOut = !!opt.eligible_variant?.product?.sold_out
                      return (
                      <button
                        key={opt.id}
                        disabled={soldOut}
                        onClick={() => setSelectedMealParts(prev => ({ ...prev, [Number(group.id)]: Number(opt.eligible_variant.id) }))}
                        className={`w-full p-3 border rounded-lg text-left ${
                          soldOut
                            ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                            : selectedMealParts[Number(group.id)] === Number(opt.eligible_variant.id) ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{opt.eligible_variant.product?.name} {opt.eligible_variant.name}</div>
                            {soldOut && <div className="text-xs font-semibold uppercase text-gray-500">Sold out</div>}
                          </div>
                          {opt.price_delta_cents !== 0 && (
                            <span className={`text-sm font-medium ${opt.price_delta_cents > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {(opt.price_delta_cents > 0 ? '+' : '') + '€' + (opt.price_delta_cents / 100).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </button>
                      )
                    })}
                    {(group.options || []).length === 0 && (
                      <div className="text-sm text-gray-500">No items available for this part.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === steps.length ? (editItem ? 'Update Cart' : 'Add to Cart') : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BundleWizard


